// src/services/demoApi.js
import axios from "axios";

const DEMO_API_URL =
  process.env.REACT_APP_DEMO_API_URL || "http://localhost:5001/api";

const demoApi = axios.create({
  baseURL: DEMO_API_URL,
  timeout: 30000,
});

// Unwrap axios envelope so callers get the JSON body directly,
// matching the behaviour of the live apiService interceptor.
demoApi.interceptors.response.use(
  (response) => response.data,
  (error) => Promise.reject(error),
);

demoApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("axoaccessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

demoApi.getCompanyScore = (companyId) =>
  demoApi.get(`/companies/${companyId}/score`);

// Demo users never trigger a real website snapshot, just fetch the score.
demoApi.refreshWebsiteHealth = (companyId) =>
  demoApi.get(`/companies/${companyId}/score`);

// Phase 5, items 2/3/5, not available in the demo, so return empty shapes
// rather than letting the demo UI issue 404s.
demoApi.getInboundReplies = async () => ({ replies: [] });
demoApi.getCompanyDossier = async () => ({ dossier: null });
demoApi.getLatestCompanyResearch = async () => ({ research: null });
demoApi.generateCompanyResearch = async () => ({ summaryText: "" });
demoApi.getChannelActions = async () => ({ actions: [] });
demoApi.recordChannelAction = async () => ({ success: true });
demoApi.sendChannelAction = async () => ({ success: true });

// Phase 5A, Daily briefing / Sales Workspace / AI Cost Transparency. The
// demo backend doesn't run the real (spend-incurring) AI endpoints, so these
// resolve to empty shells: the UI shows its empty states instead of 404ing
// or accidentally triggering real AI spend.
demoApi.getTodayBriefing = async () => ({ briefing: null });
demoApi.refreshBriefing = async () => ({ cached: false, requiresGeneration: true, briefing: null });
demoApi.estimateBriefing = async () => ({ estimate: null });
demoApi.rejectBriefingEstimate = async () => ({ success: true });
demoApi.generateBriefing = async () => ({ briefing: null });
demoApi.estimateOutreachDraft = async () => ({
  estimate: { logId: null, estimatedCostGbp: 0 },
});
demoApi.rejectOutreachEstimate = async () => ({ success: true });
demoApi.prepareOutreachDraft = async () => ({});
demoApi.getOutreachDrafts = async () => ({ drafts: [], contact: null });
demoApi.updateOutreachDraft = async () => ({ success: true });
demoApi.approveAndSendOutreach = async () => ({ success: true });
demoApi.bulkApproveAndSendOutreach = async (draftIds = []) => ({
  success: true,
  summary: {
    totalRequested: draftIds.length,
    sent: draftIds.length,
    failed: 0,
    results: draftIds.map((draftId) => ({ draftId, success: true })),
  },
});
demoApi.rejectOutreach = async () => ({ success: true });
demoApi.estimateCompanyResearch = async () => ({
  estimate: { logId: null, estimatedCostGbp: 0 },
});
demoApi.rejectCompanyResearchEstimate = async () => ({ success: true });
demoApi.recordRevenueOutcome = async () => ({ success: true });
demoApi.triggerEnrichment = async () => ({ success: true });
demoApi.refreshCompanyFinancials = async () => ({ success: true, data: null });

// Phase 5B/5C, pipeline board, AI performance and revenue forecast.
demoApi.getPipelineBoard = async () => ({ board: [] });
demoApi.movePipelineStage = async () => ({ success: true });
demoApi.getAiPerformanceDashboard = async () => ({ dashboard: null });
demoApi.getAiPerformanceTimeseries = async () => ({ timeseries: null });
demoApi.getStageTimings = async () => ({ timings: null });
demoApi.getOutreachPerformance = async () => ({ performance: null });
demoApi.getInsights = async () => ({ insights: null });
demoApi.getForecast = async () => ({ forecast: null });

export default demoApi;
